import { useVirtualizer } from '@tanstack/react-virtual';
import * as React from 'react';

// 🎯 Importaciones de componentes
import { EmptyState, LoadingItem } from './components';
import { useContainerDimensions, useInfiniteScroll } from './hooks';
import { LIST_CONSTANTS, VirtualListProps } from './types';

// 🎯 VirtualList component with Render Props Pattern
function VirtualListComponent<T = any>({
  data,
  renderItem,
  itemKey,
  height = 'calc(100vh - 160px)',
  itemHeight,
  estimateItemHeight = LIST_CONSTANTS.DEFAULT_ESTIMATE_HEIGHT,
  gap = LIST_CONSTANTS.DEFAULT_GAP,
  overscan = LIST_CONSTANTS.DEFAULT_OVERSCAN,
  className = '',
  listClassName = '',
  itemClassName = '',
  emptyMessage = 'No hay elementos disponibles',
  fetchNextPage,
  hasNextPage = false,
  isFetchingNextPage = false,
  infiniteScrollThreshold = LIST_CONSTANTS.DEFAULT_SCROLL_THRESHOLD,
  autoHeight = false,
  maxHeight,
  minHeight = '0px',
}: VirtualListProps<T>) {
  // 🎯 Refs for DOM manipulation
  const parentRef = React.useRef<HTMLDivElement>(null);

  // 🎯 Custom hooks - Dependency Inversion Principle
  const { width: _containerWidth } = useContainerDimensions(parentRef);

  // 🎯 Infinite scroll hook
  useInfiniteScroll({
    parentRef,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    threshold: infiniteScrollThreshold,
  });

  // 🎯 Determinar si usar altura fija o dinámica
  const isDynamicHeight = itemHeight === undefined;
  const effectiveItemHeight = itemHeight || estimateItemHeight;

  // 🎯 Memoized height calculation for performance
  const calculateContainerHeight = React.useMemo(() => {
    if (!autoHeight) return height;

    // Para altura dinámica, usar estimación más conservadora
    const estimatedHeight = isDynamicHeight
      ? data.length * (estimateItemHeight + gap)
      : data.length * (effectiveItemHeight + gap);

    const loadingHeight = isFetchingNextPage ? LIST_CONSTANTS.LOADING_ITEM_HEIGHT : 0;
    const borderAndPadding = 4;

    const totalContentHeight = estimatedHeight + loadingHeight + borderAndPadding;
    const maxHeightValue = maxHeight || height;

    return `min(${totalContentHeight}px, ${maxHeightValue})`;
  }, [
    autoHeight,
    height,
    effectiveItemHeight,
    gap,
    data.length,
    isFetchingNextPage,
    maxHeight,
    isDynamicHeight,
    estimateItemHeight,
  ]);

  // 🎯 Virtual row configuration with dynamic height support
  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => parentRef.current,
    // 🎯 Si itemHeight está definido, usarlo; si no, usar estimación
    estimateSize: React.useCallback(() => {
      return isDynamicHeight ? estimateItemHeight : effectiveItemHeight + gap;
    }, [isDynamicHeight, estimateItemHeight, effectiveItemHeight, gap]),
    overscan,
    gap,
    // 🎯 measureElement es crucial para alturas dinámicas
    measureElement: isDynamicHeight
      ? (element) => {
          if (!element) return estimateItemHeight;
          const height = element.getBoundingClientRect().height;
          return height || estimateItemHeight;
        }
      : typeof window !== 'undefined' && navigator.userAgent.includes('Firefox')
        ? undefined
        : (element) => element?.getBoundingClientRect().height ?? effectiveItemHeight,
  });

  // 🎯 Container height calculation
  const containerHeight = autoHeight ? calculateContainerHeight : height;

  // 🎯 Memoized container styles for performance
  const containerStyles = React.useMemo(
    () => ({
      height: containerHeight,
      minHeight: autoHeight ? minHeight : undefined,
    }),
    [containerHeight, autoHeight, minHeight],
  );

  // 🎯 Key generator function - fallback to index if no itemKey provided
  const getItemKey = React.useCallback(
    (item: T, index: number): string | number => {
      return itemKey ? itemKey(item, index) : index;
    },
    [itemKey],
  );

  // 🎯 Early return for empty state
  if (!data || data.length === 0) {
    return <EmptyState message={emptyMessage} height={autoHeight ? minHeight : height} className={className} />;
  }

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      className={`w-full rounded-[10px] overflow-hidden ${className} max-w-[100%] flex flex-col`}
      style={containerStyles}
    >
      <div
        ref={parentRef}
        className={`flex-1 table-scrollbar-minimal overflow-auto max-w-full smooth-scroll ${listClassName}`}
        style={{ height: containerHeight }}
      >
        {/* 🎯 Virtual container */}
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {/* 🎯 Virtual items */}
          {virtualItems.map((virtualItem) => {
            const item = data[virtualItem.index];
            const key = getItemKey(item, virtualItem.index);

            return (
              <div
                key={key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                className={`absolute top-0 left-0 w-full ${itemClassName}`}
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                  // 🎯 Solo aplicar altura fija si itemHeight está definido
                  ...(isDynamicHeight ? {} : { height: `${effectiveItemHeight}px` }),
                }}
              >
                {/* 🎯 Render Props Pattern - máxima flexibilidad */}
                {renderItem({
                  item,
                  index: virtualItem.index,
                  isLoading: false,
                })}
              </div>
            );
          })}

          {/* 🎯 Loading indicator */}
          {isFetchingNextPage && (
            <div
              style={{
                position: 'absolute',
                top: `${virtualizer.getTotalSize()}px`,
                left: 0,
                width: '100%',
              }}
            >
              <LoadingItem height={LIST_CONSTANTS.LOADING_ITEM_HEIGHT} />
            </div>
          )}
        </div>
      </div>

      {/* 🎯 Footer with total count (optional)
            {total !== undefined && total > 0 && (
                <div className="border-t bg-muted/5 px-4 py-3 text-sm text-muted-foreground">
                    Total: {total.toLocaleString()} elementos
                </div>
            )} */}
    </div>
  );
}

// 🎯 Crear el componente memoizado con tipos correctos
export const VirtualList = React.memo(VirtualListComponent) as typeof VirtualListComponent & {
  displayName?: string;
};

// 🎯 Display name for React DevTools
VirtualList.displayName = 'VirtualList';
